import {
  Component,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { DrawerType } from '@shared/enums/drawer-type.enum';
import { AiDrawerService } from '../../services/ai-drawer.service';
import { BaseComponent } from '@shared/components/base-component/base.component';
import { AiMessageAuthor } from '@shared-features/enums';
import { AutoGrowHeightDirective } from '@shared-features/directives/auto-grow-height.directive';
import { IChatMessage, IAiMessageDataPreview } from '@shared-features/models/ai-message.model';
import { MatTableDataSource } from '@angular/material/table';
import { finalize, takeUntil } from 'rxjs/operators';
@Component({
  selector: 'app-ai-drawer',
  templateUrl: './ai-drawer.component.html',
  styleUrls: ['./ai-drawer.component.scss'],
})
export class AiDrawerComponent extends BaseComponent implements OnInit {
  drawerType: DrawerType = null;
  isShown = false;
  message: string = '';
  messages: IChatMessage[] = [];
  isSending = false;

  @Output() drawerOpened = new EventEmitter<{ open: true; type: DrawerType }>();
  @Output() drawerClosed = new EventEmitter<void>();
  @ViewChild('messagesContainer', { static: true })
  messagesContainer!: ElementRef<HTMLDivElement>;
  @ViewChild('messageInput', { read: AutoGrowHeightDirective })
  messageInputDirective!: AutoGrowHeightDirective;
  protected readonly AiMessageAuthor = AiMessageAuthor;
  constructor(private aiDrawerService: AiDrawerService, private renderer: Renderer2) {
    super();
  }

  ngOnInit(): void {
    this.aiDrawerService.aiDrawer$.subscribe((shown: boolean) => {
      if (!shown && this.isShown) {
        this.drawerClosed.emit();
      } else if (shown) {
        this.drawerOpened.emit({ open: true, type: DrawerType.AI });
      }
      this.isShown = shown;
    });
  }
  closeDrawer(): void {
    this.drawerClosed.emit();
  }

  sendMessage(event?: KeyboardEvent) {
    if (this.isSending) return;

    if (event && event.shiftKey) {
      // Allow shift+enter for new lines
      return;
    }
    event?.preventDefault();

    const message = this.message.trim();
    if (!message) return;
    this.isSending = true;
    this.showMessage(message, AiMessageAuthor.User);
    this.message = '';
    this.messageInputDirective.resetHeight(); // Reset textarea height

    const placeholderMessage = this.createLoadingMessage();

    this.messages.push(placeholderMessage);
    this.scrollToBottom();

    this.aiDrawerService
      .sendAiMessage(message)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.isSending = false;
          placeholderMessage.isLoading = false; // remove placeholder
          this.scrollToBottom();
        })
      )
      .subscribe(
        (response) => {
          // Update placeholder with real data
          placeholderMessage.content = response.answer;

          placeholderMessage.table = this.buildTable(response.dataPreview);
        },
        (error) => {
          placeholderMessage.content = 'Something went wrong, please contact support';
          this.showErrorMessage(null, 'ErrorMessages.SomethingWentWrong');
        }
      );
  }

  private showMessage(
    content: string,
    author: AiMessageAuthor,
    dataPreview?: IAiMessageDataPreview
  ) {
    const botMessage: IChatMessage = {
      content: content,
      author: author,
      table: this.buildTable(dataPreview),
    };

    this.messages.push(botMessage);
    // Scroll to bottom after view updates
    this.scrollToBottom();
  }

  private scrollToBottom(): void {
    try {
      const container = this.messagesContainer.nativeElement;

      // Modern smooth scrolling API
      if ('scrollBehavior' in document.documentElement.style) {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: 'smooth',
        });
      }
      // Fallback for older browsers
      else {
        this.renderer.setProperty(container, 'scrollTop', container.scrollHeight);
      }

      // Safety check for async content
      setTimeout(() => {
        const isAtBottom =
          container.scrollHeight - container.scrollTop <= container.clientHeight + 5;
        if (!isAtBottom) {
          this.renderer.setProperty(container, 'scrollTop', container.scrollHeight);
        }
      }, 100);
    } catch (err) {
      console.error('Scroll error:', err);
    }
  }
  private buildTable(dataPreview?: IAiMessageDataPreview) {
    if (!dataPreview?.columns?.length) return undefined;

    return {
      columns: dataPreview.columns,
      dataSource: new MatTableDataSource(dataPreview.rows),
    };
  }
  private createLoadingMessage(): IChatMessage {
    return {
      content: '',
      author: AiMessageAuthor.Bot,
      table: undefined,
      isLoading: true,
    };
  }
}
